import { Badge, Card, Center, Group, Image, Loader, Notification, SimpleGrid, Stack, Title, useMatches } from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { Temporal } from 'temporal-polyfill'
import type { Movie, TimesForMovie } from '../../api_types.ts'
import { MovieActions } from '../components/movie_actions.tsx'
import RottenTomatoesReview from '../components/rotten_tomatoes_review.tsx'
import { trpc } from '../queryClient.ts'
import { rootRoute } from '../root.tsx'
import { formatFullDate } from '../tools/dates.ts'
import { useErrorNotifier } from '../tools/errors.tsx'

export const movieInfoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/movie/$movieId',
  component: MovieInfo,
})

function MovieInfo() {
  const movieId = movieInfoRoute.useParams({ select: params => Number(params.movieId) })
  const { data: movie, isLoading, error } = useQuery(trpc.movie.info.queryOptions(movieId))
  useErrorNotifier('Error fetching movie info', error)

  if (isLoading) return <Center h="100px"><Loader type="dots"/></Center>
  if (error) {
    return (
      <Notification withCloseButton={false} title="An error occurred fetching movies" color="red">
        {error.message}
      </Notification>
    )
  }
  if (!movie) return <div>No movie data found</div>


  return (
    <Stack>
      <MovieSummary movie={movie}/>
      <MovieTimes times={movie.times}/>
    </Stack>
  )
}

function MovieSummary({ movie }: { movie: Movie }) {
  const summaryWrap = useMatches<'wrap' | 'nowrap'>({ base: 'wrap', xs: 'nowrap' })
  const actionsFullWidth = useMatches({ base: true, sm: false })
  const queryClient = useQueryClient()

  function onMovieUpdate() {
    void queryClient.invalidateQueries({ queryKey: trpc.moviesInCinemas.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.upcomingMovies.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.movie.all.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.movie.info.queryKey(movie.id) })
  }

  const { mutate: setHidden, isPending: hidePending, error: hideError } = useMutation(trpc.user.setMovieHidden.mutationOptions({ onSuccess: onMovieUpdate }))
  useErrorNotifier('Error hiding movie', hideError)
  const { mutate: setSaved, isPending: savePending, error: saveError } = useMutation(trpc.user.setMovieSaved.mutationOptions({ onSuccess: onMovieUpdate }))
  useErrorNotifier('Error hiding movie', saveError)

  return (
    <Group wrap={summaryWrap} gap="xl" align="flex-start">
      <Image
        w={{ base: 250, xs: 185 }}
        h={{ base: 380, xs: 278 }}
        maw="100%"
        m="0 auto"
        alt={`Poster for ${movie.title}`}
        src={movie.posterPath}
      />
      <Stack style={{ flexGrow: 1 }}>
        <Title>{movie.title}</Title>
        <RottenTomatoesReview tomatometer={movie.tomatometer} title={movie.title}/>
        <MovieActions
          movie={movie}
          size="lg"
          fullWidth={actionsFullWidth}
          isSaving={savePending}
          isHiding={hidePending}
          onSave={() => setSaved({ movieId: movie.id, saved: !movie.saved })}
          onHide={() => setHidden({ movieId: movie.id, hidden: !movie.hidden })}
        />
      </Stack>
    </Group>
  )
}

function MovieTimes({ times }: { times: TimesForMovie[] }) {
  const { data: cinemas, isLoading: loadingCinemas, error: cinemaError } = useQuery(trpc.cinema.all.queryOptions())
  useErrorNotifier('Error fetching cinema info', cinemaError)

  const timesByCinema = useMemo(() => {
    const result: Record<string, string[]> = {}
    for (const { cinemaId, time } of times) {
      if (!result[cinemaId]) {
        result[cinemaId] = []
      }
      result[cinemaId].push(time)
    }
    return result
  }, [times])

  const cinemasById = useMemo(() => {
    return Object.fromEntries((cinemas || []).map(cinema => [cinema.id, cinema]))
  }, [cinemas])

  if (cinemaError) return <div>Error fetching data</div>
  if (loadingCinemas) return <div>Loading cinema times...</div>
  if (!timesByCinema) return <div>No cinema times found</div>

  return (
    <SimpleGrid spacing="xs" cols={{ xs: 1, sm: 2, lg: 3 }}>
      {Object.entries(timesByCinema).map(([cinema, times]) => (
        <Card key={cinema}>
          <Title order={2} mb="xs">{cinemasById[cinema]?.name} ({cinemasById[cinema]?.location})</Title>
          <CinemaTimes times={times}/>
        </Card>
      ))}
    </SimpleGrid>
  )
}

function CinemaTimes({ times }: { times: string[] }) {
  const timesSplitByDay = useMemo(() => splitByDay(times), [times])

  return (
    <Stack>
      {timesSplitByDay.map(timesForDay => (
        <TimesForDay key={timesForDay[0]!.dayOfYear} times={timesForDay}/>
      ))}
    </Stack>
  )
}

function TimesForDay({ times }: { times: Temporal.ZonedDateTime[] }) {
  const dayTitle = useMemo(() => formatFullDate(times[0]!), [times])
  return (
    <div>
      <Title order={4} mb="xs">
        {dayTitle}
      </Title>
      <Group gap="xs">
        {times.map(time => (
          <Badge key={time.toString()} size="lg">
            {time.toLocaleString('en-IE', { hour: '2-digit', minute: '2-digit' })}
          </Badge>
        ))}
      </Group>
    </div>
  )
}

function splitByDay(times: string[]): Temporal.ZonedDateTime[][] {
  const zonedTimes = times.map(time => Temporal.Instant.from(time).toZonedDateTimeISO(Temporal.Now.timeZoneId()))
  const zonedTimesByDay = Object.groupBy(zonedTimes, time => time.dayOfYear)
  return Object.values(zonedTimesByDay).toSorted() as Temporal.ZonedDateTime[][]
}

function formatTime(isoString: string) {
  const zonedTime = Temporal.Instant.from(isoString).toZonedDateTimeISO(Temporal.Now.timeZoneId())
  return `${zonedTime.day.toString().padStart(2, '0')}th ${zonedTime.hour.toString().padStart(2, '0')}:${zonedTime.minute.toString().padStart(2, '0')}`
}
